import { List } from "@mui/material";
import { collection, getDocs, query, where, orderBy, limit, startAfter } from 'firebase/firestore';
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { Typography } from "@mui/material/node";
import { CommentItem } from "./commentItem";
import { LoadingCircle } from "../../loading";
import { db } from "../../../config/firebase";
import { useEffect, useRef } from "react";
import { CustomContentBlock } from "./customContentBlock";
import { useQueryClient } from "@tanstack/react-query";

export const CommentsList = ({projectId, project}) => {
    const observerRef = useRef(null);
    const BATCH_SIZE = 10;
    const CUSTOM_CONTENT_INTERVAL = 3; // Insert custom content every 3 comments

    // Example custom content - in a real app, this could come from props or a configuration
    const customContents = [
        "Sponsored Content",
        "Advertisement",
        "Featured Content"
    ];

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        isError
    } = useInfiniteQuery({
        queryKey: ["loadmycomments", projectId],
        queryFn: async ({ pageParam = null }) => {
            let q;
            if (pageParam) {
                q = query(
                    collection(db, "project", projectId, "comments"),
                    orderBy("createdon", "desc"),
                    startAfter(pageParam),
                    limit(BATCH_SIZE)
                );
            } else {
                q = query(
                    collection(db, "project", projectId, "comments"),
                    orderBy("createdon", "desc"),
                    limit(BATCH_SIZE)
                );
            }
            
            const data = await getDocs(q);
            const lastVisible = data.docs[data.docs.length - 1];
            
            return {
                comments: data.docs.map((doc) => ({
                    ...doc.data(),
                    id: doc.id
                })),
                lastVisible
            };
        },
        getNextPageParam: (lastPage) => lastPage.lastVisible,
    });

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            },
            { threshold: 1.0 }
        );

        if (observerRef.current) {
            observer.observe(observerRef.current);
        }

        return () => {
            if (observerRef.current) {
                observer.unobserve(observerRef.current);
            }
        };
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    if (isLoading) return (<LoadingCircle/>);

    if (isError) return (
        <h1>Ou ... Can't load comments :(</h1>
    );

    const allComments = data?.pages.flatMap(page => page.comments) || [];

    if (!isLoading && !isError && allComments.length === 0) return (
        <Typography>No comments yet.</Typography>
    );

    // Function to render comments with custom content blocks
    const renderCommentsWithCustomContent = () => {
        const elements = [];
        
        allComments.forEach((comment, index) => {
            // Add the comment
            elements.push(
                <CommentItem key={comment.id} comment={comment} />
            );
            
            // Add custom content block after every CUSTOM_CONTENT_INTERVAL comments
            if ((index + 1) % CUSTOM_CONTENT_INTERVAL === 0) {
                const contentIndex = Math.floor(index / CUSTOM_CONTENT_INTERVAL) % customContents.length;
                elements.push(
                    <CustomContentBlock 
                        key={`custom-${index}`} 
                        content={customContents[contentIndex]} 
                    />
                );
            }
        });
        
        return elements;
    };

    return (
        <List>
            {renderCommentsWithCustomContent()}
            <div ref={observerRef} style={{ height: "20px" }}>
                {isFetchingNextPage && <LoadingCircle />}
            </div>
        </List>
    );
};